import React, { useState, useEffect } from 'react';
import { FaBell, FaBellSlash, FaCog } from 'react-icons/fa';
import { useTheme } from '@theme/useTheme';

interface AutoLoginSettingsProps {
  onClose?: () => void;
}

const AutoLoginSettings: React.FC<AutoLoginSettingsProps> = ({ onClose }) => {
  const { colors } = useTheme();
  const [isEnabled, setIsEnabled] = useState(true);
  const [delay, setDelay] = useState(10); // seconds

  useEffect(() => {
    // Load settings from localStorage
    const storedEnabled = localStorage.getItem('auto_login_modal_enabled');
    const storedDelay = localStorage.getItem('auto_login_modal_delay');
    
    if (storedEnabled !== null) {
      setIsEnabled(storedEnabled === 'true');
    }
    
    if (storedDelay !== null) {
      setDelay(parseInt(storedDelay, 10));
    }
  }, []);

  const handleToggleEnabled = () => {
    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);
    localStorage.setItem('auto_login_modal_enabled', newEnabled.toString());
  };

  const handleDelayChange = (newDelay: number) => {
    setDelay(newDelay);
    localStorage.setItem('auto_login_modal_delay', newDelay.toString());
  };

  const delayOptions = [
    { value: 5, label: '5 seconds' },
    { value: 10, label: '10 seconds' },
    { value: 15, label: '15 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-4">
        <FaCog 
          size={20} 
          style={{ color: colors.primary }}
        />
        <h3 
          className="text-lg font-semibold"
          style={{ color: colors.heading }}
        >
          Auto-Login Settings
        </h3>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl border-2"
           style={{ 
             backgroundColor: colors.input,
             borderColor: colors.primary 
           }}>
        <div className="flex items-center space-x-3">
          {isEnabled ? (
            <FaBell 
              size={18} 
              style={{ color: colors.primary }}
            />
          ) : (
            <FaBellSlash 
              size={18} 
              style={{ color: colors.text }}
            />
          )}
          <div>
            <p 
              className="font-medium"
              style={{ color: colors.heading }}
            >
              Show login reminder
            </p>
            <p 
              className="text-sm"
              style={{ color: colors.text }}
            >
              Automatically show login modal for new visitors
            </p>
          </div>
        </div>
        <button
          onClick={handleToggleEnabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
            isEnabled ? 'bg-green-500' : 'bg-gray-300'
          }`}
          aria-label={isEnabled ? 'Disable auto-login reminder' : 'Enable auto-login reminder'}
          title={isEnabled ? 'Disable auto-login reminder' : 'Enable auto-login reminder'}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
              isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Delay Settings */}
      {isEnabled && (
        <div className="p-4 rounded-xl border-2"
             style={{ 
               backgroundColor: colors.input,
               borderColor: colors.primary 
             }}>
          <p 
            className="font-medium mb-3"
            style={{ color: colors.heading }}
          >
            Show reminder after:
          </p>
          <div className="grid grid-cols-2 gap-2">
            {delayOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleDelayChange(option.value)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  delay === option.value
                    ? 'text-white'
                    : 'hover:bg-gray-100'
                }`}
                style={{
                  backgroundColor: delay === option.value ? colors.primary : 'transparent',
                  color: delay === option.value ? 'white' : colors.text
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Info Text */}
      <div 
        className="p-3 rounded-lg text-sm"
        style={{ backgroundColor: colors.secondaryButton }}
      >
        <p style={{ color: colors.text }}>
          {isEnabled 
            ? `The login reminder will appear after ${delay} seconds of inactivity for users who aren't signed in.`
            : 'Login reminders are disabled. Users will need to manually click the login button.'
          }
        </p>
      </div>

      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          className="w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200"
          style={{ 
            backgroundColor: colors.primary,
            color: 'white'
          }}
        >
          Done
        </button>
      )}
    </div>
  );
};

export default AutoLoginSettings;
